import classNames from "./EventSpeaker.module.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LiveKitRoom, useLocalParticipant } from "@livekit/components-react";
import useUser, { useUserMetadata } from "../../../hooks/user";
import Captions from "../../common/Captions";
import { usePublisherToken } from "../../../hooks/token";
import { useAxios } from "../../../hooks/axios";
import UserLanguageSelect from "../../common/UserLanguageSelect";
import Chat from "../../common/Chat";
import {
  getLanguageFlag,
  getLanguageName,
  initialCaptionsState,
} from "../../../types";
import Loader from "../../common/Loader";
import { useRecordingToast } from "../../../hooks/recording";
import {
  usePollRoomMinutesRemaining,
  useRoom,
  useWakeLock,
} from "../../context/room/room";
import VoiceOptionsDialog from "../../common/dialogs/voice/VoiceOptionsDialog";
import { useDefaultSettings } from "../../../hooks/settings";
import ConfirmLeaveDialog from "../../common/dialogs/ConfirmLeaveDialog";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../common/Button";
import {
  IoChevronDown,
  IoClose,
  IoHandRightOutline,
  IoSettingsOutline,
} from "react-icons/io5";
import RadixDialog from "../../common/dialogs/RadixDialog";
import { IoMdAdd } from "react-icons/io";
import LanguageSelect from "../../common/LanguageSelect";
import { useAudioEnabled } from "../../../hooks/media";
import SettingsDialog from "../../common/dialogs/SettingsDialog";
import EventTitles from "./EventTitles";
import { LocalAudioTrack } from "livekit-client";
import ToggleMicDialog from "./ToggleMicDialog";
import UpgradeNow from "../../common/UpgradeNow";
import PinDialog from "./PinDialog";
import RecordingStatus from "../../common/RecordingStatus";
import { useIsMobile } from "../../../hooks/layout";
import OverflowPopover from "./OverflowPopover";
import { useListenForChatMessages } from "../../../hooks/chat";
import useCaptions from "../../../hooks/captions";

const AddLanguageDialog = (props: {
  languages: string[];
  onAdd: (language: string) => void;
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(props.languages[0]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!props.languages.includes(selectedLanguage)) {
      setSelectedLanguage(props.languages[0]);
    }
  }, [selectedLanguage, props.languages]);

  return (
    <RadixDialog
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button
          color="gray"
          backgroundColor="transparent"
          borderColor="dark-gray"
          style={{ height: "100%", maxHeight: "100%", padding: "0px 8px" }}
          size="large"
        >
          <IoMdAdd />
        </Button>
      }
      title={"Add a new translated caption"}
      content={
        <div className={classNames.AddLanguageDialogContent}>
          <div>
            <h3>Which language would you like to add a caption in?</h3>
            <LanguageSelect
              color="gray"
              backgroundColor="transparent"
              borderColor="dark-gray"
              languages={props.languages}
              selectedLanguage={selectedLanguage}
              onChange={(language) => setSelectedLanguage(language)}
            />
          </div>
          <Button
            style={{ width: "100%" }}
            onClick={() => {
              setOpen(false);
              props.onAdd(selectedLanguage);
            }}
          >
            Add
          </Button>
        </div>
      }
    />
  );
};

function SpeakerDashboard() {
  useUserMetadata();
  useDefaultSettings();
  usePollRoomMinutesRemaining();
  const { room, organization, loading } = useRoom();
  const [user] = useUser();

  useWakeLock();

  const [renderLanguages, setRenderLanguages] = useState<string[]>([]);

  useRecordingToast({
    inProgressToast: (
      <div className="Toast">
        <div>{"Recording in progress"}</div>
      </div>
    ),
    stopToast: (
      <div className="Toast">
        <div>{"Recording stopped"}</div>
      </div>
    ),
  });

  const initRenderLanguages = useRef(false);
  useEffect(() => {
    if (!room || initRenderLanguages.current) {
      return;
    }
    initRenderLanguages.current = true;
    setRenderLanguages(room.languages.slice(0, 4));
  }, [room]);

  const addLanguage = (lang: string) => {
    setRenderLanguages((prevState) => [...prevState, lang]);
  };

  const [showQna, setShowQna] = useState(false);

  const [numUnreadMessages, setNumUnreadMessages] = useState(0);

  useEffect(() => {
    if (showQna) {
      setNumUnreadMessages(0);
    }
  }, [showQna]);

  const [audioEnabled, setAudioEnabled] = useAudioEnabled();

  // Set audio initial state to false
  const setInitialAudioState = useRef(false);
  useEffect(() => {
    if (!setInitialAudioState.current) {
      setInitialAudioState.current = true;
      setAudioEnabled(false);
    }
  }, [setAudioEnabled]);

  const { microphoneTrack: microphoneTrackPublication } = useLocalParticipant();
  const localAudioTrack = useMemo(() => {
    return microphoneTrackPublication?.audioTrack as
      | LocalAudioTrack
      | undefined;
  }, [microphoneTrackPublication]);

  useListenForChatMessages();
  const onReceivedMessages = useCallback(
    (numMessages: number) => {
      if (!showQna) {
        setNumUnreadMessages((prev) => prev + numMessages);
      }
    },
    [showQna]
  );
  const isMobile = useIsMobile();

  if (!room || !organization || loading) {
    return <Loader />;
  }

  return (
    <div className={classNames.EventSpeaker}>
      <div className={classNames.Panels}>
        <div className={classNames.TitlesAndCaptions}>
          <div className={classNames.Titles}>
            <EventTitles room={room} />
            <div className={classNames.RecordingAndUsage}>
              {!isMobile && <RecordingStatus hide={!audioEnabled} />}
              <UpgradeNow organization={organization} />
            </div>
          </div>

          <div className={classNames.CaptionsAndAdd}>
            {!isMobile ? (
              <>
                <div className={classNames.Captions}>
                  {renderLanguages.map((language) => (
                    <div key={language}>
                      <div className={classNames.CaptionTitle}>
                        <h3>{`${getLanguageFlag(language)} ${getLanguageName(
                          language
                        )}`}</h3>
                        <button
                          onClick={() =>
                            // Remove language
                            setRenderLanguages((prevState) => {
                              return prevState.filter(
                                (lang) => lang !== language
                              );
                            })
                          }
                        >
                          <IoClose />
                        </button>
                      </div>
                      <Captions
                        renderLanguage={language}
                        showSpeakerName={room.num_speakers !== 1}
                      />
                    </div>
                  ))}
                </div>
                {!(
                  renderLanguages.length >= room.languages.length ||
                  renderLanguages.length >= 4
                ) && (
                  <AddLanguageDialog
                    languages={room.languages.filter(
                      (lang) => !renderLanguages.includes(lang)
                    )}
                    onAdd={addLanguage}
                  />
                )}
              </>
            ) : (
              <>
                <Captions
                  renderLanguage={user.metadata.selectedLanguage}
                  showSpeakerName={room.num_speakers !== 1}
                />
              </>
            )}
          </div>
        </div>

        <div
          className={classNames.Qna}
          style={{ display: !showQna ? "none" : undefined }}
        >
          <Chat
            canAskQuestions={false}
            mode={"qna"}
            open={showQna}
            onOpenChange={() => setShowQna(false)}
            onReceivedMessages={onReceivedMessages}
          />
        </div>
      </div>

      <div className={classNames.Controls}>
        <div>
          <UserLanguageSelect
            languages={[
              ...(organization?.autodetect_enabled ? ["autodetect"] : []),
              ...room.languages,
            ]}
          />

          {organization.voice_translation_enabled && !isMobile && (
            <VoiceOptionsDialog
              trigger={
                <Button color="gray" backgroundColor="black">
                  Voice avatar
                  <IoChevronDown />
                </Button>
              }
              locale={user.metadata.selectedLanguage}
              basic={!organization.voice_avatar_enabled}
            />
          )}

          {room.qna && (
            <Button
              color={showQna ? "white" : "gray"}
              borderColor={showQna ? "primary" : "dark-gray"}
              backgroundColor="black"
              style={{
                position: "relative",
              }}
              onClick={() => setShowQna((prevState) => !prevState)}
            >
              <IoHandRightOutline />
              {!isMobile && "Q&A"}
              {numUnreadMessages > 0 && (
                <div className={classNames.UnreadMessages}>
                  {numUnreadMessages}
                </div>
              )}
            </Button>
          )}
        </div>

        <div className={classNames.MicToggle}>
          <h2>Translation {audioEnabled ? "on" : "off"}</h2>
          <ToggleMicDialog
            title={`Turn ${!audioEnabled ? "on" : "off"} translation`}
            description={
              !audioEnabled
                ? "Are you sure you want to start the translation for your attendees? They will see and hear translations in real-time as you speak."
                : "Are you sure you want to stop the translation for your attendees? They will no longer see and hear translations in real-time as you speak."
            }
          />
        </div>
        <div>
          {!isMobile ? (
            <SettingsDialog
              hiddenCategories={["video", "voice-avatar", "text-translation"]}
              localAudioTrack={localAudioTrack}
              trigger={
                <Button
                  color="gray"
                  borderColor="dark-gray"
                  backgroundColor="black"
                >
                  <IoSettingsOutline />
                  Settings
                </Button>
              }
            />
          ) : (
            <OverflowPopover organization={organization} type="speaker" />
          )}
          <ConfirmLeaveDialog link={`/event/${room.id}/survey`} />
        </div>
      </div>
    </div>
  );
}

function InputNameDialog({ onSubmit }: { onSubmit: () => void }) {
  const [user, setUser] = useUser();
  const { room } = useRoom();

  useEffect(() => {
    if (!room) {
      return;
    }
    if (room.num_speakers === 1) {
      onSubmit();
    }
  }, [room, onSubmit]);

  // There are 2 or more speakers, so get their names
  return (
    <RadixDialog
      title={"Enter your display name"}
      open={true}
      unclosable={true}
      content={
        <div className={classNames.InputNameDialog}>
          <div>
            <h2>Your name</h2>
            <input
              type="text"
              placeholder="Ex. Dr. Louise Banks"
              onChange={(e) =>
                setUser((prevState) => ({
                  ...prevState,
                  metadata: { ...prevState.metadata, name: e.target.value },
                }))
              }
              className={classNames.input}
              value={user.metadata.name || ""}
            />
          </div>
          <Button
            disabled={!user.metadata.name || !user.metadata.name.length}
            onClick={() => onSubmit()}
          >
            Confirm
          </Button>
        </div>
      }
    />
  );
}

function EventSpeaker() {
  const axios = useAxios();
  const { token, error: tokenError } = usePublisherToken(axios);
  const { room, loading } = useRoom();

  const [, setUser] = useUser();
  const [, setCaptions] = useCaptions();

  useEffect(() => {
    if (!room) {
      return;
    }
    setUser((prevState) => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        participantType: "speaker",
        selectedLanguage: room.languages[0],
      },
    }));

    return () => {
      // Set participant type to undefined when they leave the event
      setUser((prevState) => ({
        ...prevState,
        metadata: {
          ...prevState.metadata,
          participantType: undefined,
        },
      }));
      setCaptions(initialCaptionsState);
    };
  }, [room, setUser, setCaptions]);

  const [submittedName, setSubmittedName] = useState(false);

  const navigate = useNavigate();
  useEffect(() => {
    if (!room) {
      return;
    }
    if (tokenError && tokenError.response?.data.message === "EVENT_ENDED") {
      toast(
        <div className="Toast">
          <div>{"This event has ended, thanks for joining us"}</div>
        </div>
      );
      navigate(`/event/${room.id}/survey`);
      return;
    }
    if (
      tokenError &&
      tokenError.response?.data.message === "EVENT_NOT_STARTED"
    ) {
      toast(
        <div className="Toast">
          <div>{"This event hasn't started yet, stay tuned"}</div>
        </div>
      );
      navigate(`/`);
      return;
    }
  }, [tokenError, navigate, room]);

  const [searchParams, setSearchParams] = useSearchParams();

  if (!room || loading) {
    return <Loader />;
  }

  // TODO: Get room pin (if not in url), use in token issuance
  if (!searchParams.has("pin")) {
    return (
      <PinDialog
        onSubmit={(pin) =>
          setSearchParams((prevState) => {
            prevState.set("pin", pin);
            return prevState;
          })
        }
      />
    );
  }

  // Then get speaker name (if needed)
  if (!submittedName && (room.num_speakers ?? 1) > 1) {
    return <InputNameDialog onSubmit={() => setSubmittedName(true)} />;
  }

  return (
    <>
      <LiveKitRoom
        audio={false}
        video={false}
        token={token}
        connectOptions={{ autoSubscribe: false }}
        serverUrl={import.meta.env.VITE_LIVEKIT_URL}
        style={{
          width: "100%",
          height: "fit-content",
        }}
      >
        <SpeakerDashboard />
      </LiveKitRoom>
    </>
  );
}

export default EventSpeaker;
